/**
 * Example Usage:
 * class MyComponent extends React.Component {
 *   render() {
 *     const { feature1, feature2 } = this.props.featurePermissions;
 *       return (
 *         <div>
 *           <Component1 canExport={feature1.export} />
 *           <Component2 canRead={feature2.read} canUpdate={feature2.update}/>
 *         </div>
 *       );
 *   }
 * }
 *
 * export default compose(
 *   withFeaturePermissions(props => ({
 *     featurePermissions: {
 *       courseId: props.courseId,
 *       features: {
 *         feature1: {
 *           name: 'authoringCourseGrades',
 *           verbs: ['export'],
 *           instanceId: props.courseId,
 *           scopeType: 'COURSE',
 *         },
 *         feature2: {
 *           name: 'authoringCourseMessages',
 *           verbs: ['read', 'update],
 *           instanceId: props.courseId,
 *           scopeType: 'COURSE',
 *         },
 *       },
 *   })),
 *   ...
 * )(MyComponent);
 *
 * Authorization currently only supports 'COURSE' scoped permissions,
 * and single featurePermission object inputs
 * {instanceId} and {scopeType} included for forward compatibility
 *
 * In future we will support multiple feature object inputs:
 *
 * withFeaturePermissions(props => ({
 *    coursefeaturePermissions: {
 *      courseId: props.course.id,
 *      features: getCourseNavigationFeatures(props.course.id),
 *    },
 *    branchfeaturePermissions: {
 *      courseId: props.course.id,
 *      features: getBranchNavigationFeatures(props.branch.id),
 *    },
 *  }))
 *
 *
 */
import type React from 'react';

import each from 'lodash/each';
import { branch, compose, mapProps, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';
import waitForProps from 'js/lib/waitForProps';

import Naptime from 'bundles/naptimejs';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import AuthoringUserCoursePermissions from 'bundles/naptimejs/resources/authoringUserCoursePermissions.v1';
import { hasFeaturePermission } from 'bundles/teach-course/utils/featureAuthorization';
import type {
  AuthoringUserPermissions,
  CourseGroupInputFunction,
  CourseInputFunction,
  Feature,
  FeaturePermissions,
  InputFeatures,
  ScopedFeaturePermissions,
  ScopedFeatures,
} from 'bundles/teach-course/utils/types';

// See https://github.com/webedx-spark/infra-services/blob/main/services/authoring/app/org/coursera/authoring/authorization/enrichment/RoleFeaturesMappings.scala
// for defined permissions
export const getFeaturePermissions = (input: CourseInputFunction) => (Component: React.ComponentType<$TSFixMe>) =>
  compose(
    mapProps((props: $TSFixMe) => {
      const inputProps = input(props);
      const permissionsReturnKey = Object.keys(inputProps)[0];

      return {
        ...props,
        ...inputProps[permissionsReturnKey],
        permissionsReturnKey,
      };
    }),
    branch(
      // when a user is acting as learner, we need to call the AuthoringUserCoursePermissions my endpoint.
      // this is to make sure the correct permissions checks are made for the staff user.
      ({ actAsLearner = false }: { actAsLearner: boolean }) => !actAsLearner,
      Naptime.createContainer(
        ({ courseId, gracefulDegradation }: { courseId: string; gracefulDegradation: boolean }) => ({
          authoringUserPermissions: AuthoringUserCoursePermissions.get(tupleToStringKey([user.get().id, courseId]), {
            required: !gracefulDegradation,
          }),
        })
      ),
      compose(
        Naptime.createContainer(
          ({ courseId, gracefulDegradation }: { courseId: string; gracefulDegradation: boolean }) => ({
            myAuthoringUserCoursePermissions: AuthoringUserCoursePermissions.my(courseId, {
              required: !gracefulDegradation,
            }),
          })
        ),
        withProps((props: $TSFixMe) => {
          const authoringUserPermissions = props.myAuthoringUserCoursePermissions?.[0] || null;
          return {
            authoringUserPermissions,
          };
        })
      )
    ),
    mapProps(
      ({
        features,
        authoringUserPermissions,
        permissionsReturnKey,
        courseId,
        groupId,
        ...rest
      }: {
        features: InputFeatures;
        authoringUserPermissions?: AuthoringUserPermissions;
        courseId: string;
        groupId: string;
        permissionsReturnKey: string;
      }) => {
        const allPropsToPassThrough = { ...rest, courseId, groupId, permissionsReturnKey };
        if (!authoringUserPermissions) {
          return {
            ...allPropsToPassThrough,
          };
        }

        const featurePermissions: ScopedFeaturePermissions = {};
        each(features, (scopedFeatures: ScopedFeatures) => {
          featurePermissions[scopedFeatures.instanceId] = {};
          each(scopedFeatures.features, (feature: Feature) => {
            featurePermissions[scopedFeatures.instanceId][feature.name] = {};
            each(feature.verbs, (verb: string) => {
              featurePermissions[scopedFeatures.instanceId][feature.name][verb] = hasFeaturePermission(
                feature.name,
                verb,
                scopedFeatures.instanceId,
                authoringUserPermissions.permissions
              );
            });
          });
        });
        return {
          featurePermissions,
          ...allPropsToPassThrough,
        };
      }
    )
  )(Component);

/**
 * Only get permissions scoped to the `courseId`, returning type `FeaturePermissions` instead
 * of returning type `ScopedFeaturePermissions`
 */
export const withCourseFeaturePermissions =
  (input: CourseInputFunction) => (Component: React.ComponentType<$TSFixMe>) =>
    compose(
      getFeaturePermissions(input),
      waitForProps(['courseId', 'featurePermissions']),
      // @ts-expect-error TSMIGRATION
      mapProps(({ courseId, featurePermissions, permissionsReturnKey, ...rest }) => ({
        courseId,
        [permissionsReturnKey]: featurePermissions[courseId],
        ...rest,
      }))
    )(Component);

interface WithCourseFeaturePermissionsMapPropsArguments {
  courseId: string;
  groupId: string;
  featurePermissions: ScopedFeaturePermissions;
  permissionsReturnKey: string;
}

/**
 * Identical to withCourseFeaturePermissions, except that it also considers the contextId to generate
 * group-scoped permissions as well.
 */
export const withCourseGroupFeaturePermissions =
  (input: CourseGroupInputFunction) => (Component: React.ComponentType<$TSFixMe>) =>
    compose(
      getFeaturePermissions(input),
      waitForProps(['courseId', 'featurePermissions']),
      mapProps(
        ({
          courseId,
          groupId,
          featurePermissions,
          permissionsReturnKey,
          ...rest
        }: WithCourseFeaturePermissionsMapPropsArguments) => {
          let returnPermissions: FeaturePermissions | undefined;

          if (featurePermissions[courseId] || featurePermissions[groupId]) {
            returnPermissions = {
              ...(featurePermissions[courseId] || {}),
              ...(featurePermissions[groupId] || {}),
            };
          }

          return {
            courseId,
            [permissionsReturnKey]: returnPermissions,
            ...rest,
          };
        }
      )
    )(Component);

/**
 * Produces similar behavior to `withCourseFeaturePermissions` but handles errors differently.
 * Unlike `withCourseFeaturePermissions`, this HOC does not wait for featurePermissions to be populated before rendering
 * the rest of the tree.
 */
export const withOptionalCourseFeaturePermissions =
  (input: CourseInputFunction) => (Component: React.ComponentType<$TSFixMe>) =>
    compose(
      withProps(() => ({
        gracefulDegradation: true,
      })),
      getFeaturePermissions(input),
      // @ts-expect-error TSMIGRATION
      mapProps(({ courseId, featurePermissions, permissionsReturnKey, ...rest }) => ({
        courseId,
        [permissionsReturnKey]: featurePermissions && featurePermissions[courseId],
        ...rest,
      }))
    )(Component);

/**
 * withOptionalCourseFeaturePermissions, but used for 'act as learner'.
 */
export const withOptionalCourseActAsLearnerFeaturePermissions =
  (input: CourseInputFunction) => (Component: React.ComponentType<$TSFixMe>) =>
    compose(
      withProps(() => ({
        gracefulDegradation: true,
        actAsLearner: true,
      })),
      getFeaturePermissions(input),
      // @ts-expect-error TSMIGRATION
      mapProps(({ courseId, featurePermissions, permissionsReturnKey, ...rest }) => ({
        courseId,
        [permissionsReturnKey]: featurePermissions && featurePermissions[courseId],
        ...rest,
      }))
    )(Component);

/**
 * Returns all feature permissions with type ScopedFeaturePermissions related to this course
 */
const withFeaturePermissions = (input: CourseInputFunction) => (Component: React.ComponentType<$TSFixMe>) =>
  compose(
    getFeaturePermissions(input),
    waitForProps(['courseId', 'featurePermissions']),
    // @ts-expect-error TSMIGRATION
    mapProps(({ courseId, featurePermissions, permissionsReturnKey, ...rest }) => ({
      courseId,
      [permissionsReturnKey]: featurePermissions,
      ...rest,
    }))
  )(Component);

export default withFeaturePermissions;
